import React, { useEffect, useImperativeHandle, useState } from 'react'
import debounce from 'lodash/debounce'
import MaterialIcon from 'react-native-vector-icons/MaterialIcons'
import I18n from 'APP/I18n'
import BottomSheetModal from 'APP/Components/BottomSheetModal'
import DialogueMarkdown from 'APP/Components/DialogueMarkdown'
import { SliderComponent } from 'APP/Converse/Slider'
import Typography from 'APP/Converse/Typography'
import { Metrics, Colors } from 'APP/Themes'
import {
  BannerContainer,
  ContentContainer,
  MarkdownContainer,
  MarkdownStyles,
  SliderContainer,
  SliderValuesContainer,
  TargetLabelContainer,
} from './style'
import Analytics from 'APP/Services/Analytics'

const TARGET_RANGE = [150, 900]
const TARGET_STEP = 10

interface ChangeTargetModalProps {
  isVisible: boolean
  onClose: () => void
  currentTarget?: number
  onSubmit: (value: number) => void
}

export interface ChangeTargetModalRef {
  reset: () => void
}

const ChangeTargetModal = React.forwardRef<ChangeTargetModalRef, ChangeTargetModalProps>(
  ({ isVisible, onClose, currentTarget = TARGET_RANGE[0], onSubmit }, ref) => {
    const [value, setValue] = useState([currentTarget])
    const resetForm = () => setValue([currentTarget])

    useEffect(() => {
      if (isVisible) {
        resetForm()
        Analytics.trackScreen('Change target')
      }
    }, [isVisible])

    useImperativeHandle(ref, () => ({
      reset: resetForm,
    }))

    const handleSubmitDebounced = debounce(() => onSubmit(value[0]), 500)

    return (
      <BottomSheetModal
        isVisible={isVisible}
        onClose={onClose}
        title={I18n.t('ActiveMinutes.changeTarget.title')}
        testID="ChangeTargetModal"
      >
        <ContentContainer>
          <TargetLabelContainer>
            <Typography variant="h1">{value?.[0]}</Typography>
            <Typography variant="bodyLarge">
              {I18n.t('ActiveMinutes.changeTarget.valueLabel')}
            </Typography>
            <Typography variant="bodySmall" color="textSecondary">
              {I18n.t('ActiveMinutes.changeTarget.valueFormat', {
                hours: Math.floor((value?.[0] || 0) / 60),
                minutes: (value?.[0] || 0) % 60,
              })}
            </Typography>
          </TargetLabelContainer>
          <SliderContainer testID="targetSliderContainer">
            <SliderComponent
              value={value}
              minValue={TARGET_RANGE[0]}
              maxValue={TARGET_RANGE[1]}
              onChange={(...args) => {
                handleSubmitDebounced.cancel()
                setValue(...args)
              }}
              onChangeFinish={handleSubmitDebounced}
              step={TARGET_STEP}
              allowOverlap
            />
            <SliderValuesContainer>
              {TARGET_RANGE.map((value) => (
                <Typography variant="bodyNormal" color="textSecondary" key={value}>
                  {I18n.t('ActiveMinutes.changeTarget.sliderValues', { value })}
                </Typography>
              ))}
            </SliderValuesContainer>
          </SliderContainer>
          <BannerContainer>
            <MaterialIcon
              name="check-circle-outline"
              size={Metrics.icons.small}
              color={Colors.text}
            />
            <MarkdownContainer>
              <DialogueMarkdown
                markdown={I18n.t('ActiveMinutes.changeTarget.bannerMarkdown')}
                styles={MarkdownStyles}
                containerStyles={{}}
              />
            </MarkdownContainer>
          </BannerContainer>
        </ContentContainer>
      </BottomSheetModal>
    )
  }
)
ChangeTargetModal.displayName = 'ChangeTargetModal'

export default ChangeTargetModal
